import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaEllipsisVertical } from "react-icons/fa6";
import { getUnmatchedMasterMenuUPCs } from "@/api/admin.js";
import Loader from "@/components/Loader.jsx";
import ModalBasic from "@/components/ModalBasic.jsx";
import PaginationNumeric from "@/components/PaginationNumeric.jsx";
import MasterMenuCreateProduct from "./MasterMenuCreateProduct.jsx";

const DEFAULT_LIMIT = 50;

const SORT_STORES_DESC = "stores_desc";
const SORT_FIRST_OBSERVED_DESC = "first_observed_desc";

function formatObserved(iso) {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const sortPillClass = (active) =>
  `inline-flex items-center justify-center text-xs font-medium leading-5 rounded-full px-3 py-1 border shadow-sm duration-150 ease-in-out ${
    active
      ? "border-theme-button-primary bg-theme-button-primary text-theme-button-primary-text"
      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
  }`;

function FixedDropdown({
  show,
  onClose,
  triggerRef,
  menuRef,
  estimatedHeight,
  align,
  children,
}) {
  const [pos, setPos] = useState({ top: 0, left: 0, right: undefined });

  useEffect(() => {
    if (!show || !triggerRef?.current) return;
    const btn = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const menuWidth = 224;
    const h = estimatedHeight;

    const wouldOverflowBottom = btn.bottom + h > vh;
    const top = wouldOverflowBottom ? btn.top - h : btn.bottom;

    if (align === "right") {
      setPos({
        top,
        right: vw - btn.right,
        left: undefined,
      });
    } else {
      let left = btn.left;
      if (left + menuWidth > vw - 8) left = Math.max(8, vw - menuWidth - 8);
      if (left < 8) left = 8;
      setPos({ top, left, right: undefined });
    }
  }, [show, triggerRef, estimatedHeight, align]);

  useEffect(() => {
    const handleOutside = (e) => {
      const inMenu = menuRef.current?.contains(e.target);
      const inTrigger = triggerRef.current?.contains(e.target);
      if (!inMenu && !inTrigger) onClose();
    };
    if (show) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [show, onClose, menuRef, triggerRef]);

  if (!show) return null;

  return (
    <div
      ref={menuRef}
      className="fixed w-56 py-2 z-50 bg-white dark:bg-theme-surface-accent rounded-sm shadow-lg border border-slate-200 dark:border-slate-700"
      style={{
        top: `${pos.top}px`,
        ...(pos.right != null ? { right: `${pos.right}px` } : { left: `${pos.left}px` }),
      }}
    >
      {children}
    </div>
  );
}

const noop = () => {};

function toCreateProductState(row) {
  return {
    autocreateFromUPC: true,
    autocreateSeed: {
      upc_code: String(row?.retailer_upc || "").trim(),
      title: String(row?.title || "").trim(),
      subtitle: "",
      company: "",
    },
  };
}

const MasterMenuUnmatchedUPCs = () => {
  const [page, setPage] = useState(1);
  const [sortMode, setSortMode] = useState(SORT_STORES_DESC);
  const [prefixInput, setPrefixInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const bulkBtnRef = useRef(null);
  const bulkMenuRef = useRef(null);

  const [rowMenuUpc, setRowMenuUpc] = useState(null);
  const rowBtnRefs = useRef({});
  const rowMenuAnchorRef = useRef(null);
  const rowMenuPanelRef = useRef(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalSeed, setCreateModalSeed] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(prefixInput.trim()), 350);
    return () => clearTimeout(t);
  }, [prefixInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, sortMode]);

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [page, debouncedQ, sortMode]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUnmatchedMasterMenuUPCs({
        page,
        limit: DEFAULT_LIMIT,
        q: debouncedQ || undefined,
        sort: sortMode,
      });
      setPayload(res?.data ?? null);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to load unmatched UPCs");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedQ, sortMode]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const items = payload?.items ?? [];
  const total = payload?.total ?? 0;
  const totalPages = Math.max(1, payload?.total_pages ?? 1);

  const visibleUpcs = useMemo(
    () => items.map((r) => r.retailer_upc),
    [items]
  );

  const allVisibleSelected =
    visibleUpcs.length > 0 && visibleUpcs.every((id) => selectedKeys.has(id));
  const someVisibleSelected =
    visibleUpcs.some((id) => selectedKeys.has(id)) && !allVisibleSelected;

  const headerCheckboxRef = useRef(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const toggleOne = useCallback((upc) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(upc)) next.delete(upc);
      else next.add(upc);
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleUpcs.forEach((id) => next.delete(id));
      } else {
        visibleUpcs.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allVisibleSelected, visibleUpcs]);

  const selectedCount = selectedKeys.size;

  useEffect(() => {
    if (selectedCount === 0) setBulkOpen(false);
  }, [selectedCount]);

  const summary = useMemo(() => {
    if (total === 0) return "No rows";
    const start = (page - 1) * DEFAULT_LIMIT + 1;
    const end = Math.min(page * DEFAULT_LIMIT, Number(total));
    return `${start.toLocaleString()}–${end.toLocaleString()} of ${Number(total).toLocaleString()}`;
  }, [total, page]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              Sort
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={sortPillClass(sortMode === SORT_STORES_DESC)}
                onClick={() => setSortMode(SORT_STORES_DESC)}
              >
                Carried By Most
              </button>
              <button
                type="button"
                className={sortPillClass(sortMode === SORT_FIRST_OBSERVED_DESC)}
                onClick={() => setSortMode(SORT_FIRST_OBSERVED_DESC)}
              >
                Newest Items
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="relative shrink-0">
            <button
              type="button"
              ref={bulkBtnRef}
              disabled={selectedCount === 0}
              onClick={() => setBulkOpen((o) => !o)}
              className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm font-medium shadow-sm ${
                selectedCount === 0
                  ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-600"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              Bulk actions
              <svg className="w-4 h-4 opacity-70" viewBox="0 0 20 20" fill="currentColor">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </button>
            <FixedDropdown
              show={bulkOpen && selectedCount > 0}
              onClose={() => setBulkOpen(false)}
              triggerRef={bulkBtnRef}
              menuRef={bulkMenuRef}
              estimatedHeight={88}
              align="left"
            >
              <button
                type="button"
                className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
                onClick={() => {
                  setBulkOpen(false);
                  noop();
                }}
              >
                Bulk Create
              </button>
              <button
                type="button"
                className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
                onClick={() => {
                  setBulkOpen(false);
                  noop();
                }}
              >
                Bulk Create &amp; Link
              </button>
            </FixedDropdown>
          </div>

          <div className="w-full sm:max-w-md min-w-0">
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              Filter by UPC prefix
            </label>
            <input
              type="search"
              className="form-input w-full"
              placeholder="e.g. 00841"
              value={prefixInput}
              onChange={(e) => setPrefixInput(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 shrink-0">{summary}</p>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="relative min-h-[200px]">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-slate-900/50 rounded">
            <Loader />
          </div>
        )}
        <div className="overflow-x-auto rounded-sm border border-slate-200 dark:border-slate-700">
          <table className="w-full table-auto text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300">
              <tr>
                <th className="pl-4 pr-2 py-3 w-px">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-indigo-600 rounded border-slate-300 dark:border-slate-600"
                    checked={allVisibleSelected && visibleUpcs.length > 0}
                    onChange={toggleSelectAllVisible}
                    disabled={visibleUpcs.length === 0 || loading}
                    aria-label="Select all on this page"
                  />
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">UPC</th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap text-right">
                  Stores
                </th>
                <th className="px-4 py-3 font-semibold min-w-[12rem]">
                  Title (first non-empty)
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  First observed
                </th>
                <th className="px-4 py-3 font-semibold whitespace-nowrap">
                  Last observed
                </th>
                <th className="px-2 py-3 w-px font-semibold text-right whitespace-nowrap">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                    No unmatched UPC rows match this filter.
                  </td>
                </tr>
              )}
              {items.map((row) => (
                <tr
                  key={row.retailer_upc}
                  className="bg-white dark:bg-theme-surface-accent hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                >
                  <td className="pl-4 pr-2 py-3 align-middle">
                    <input
                      type="checkbox"
                      className="form-checkbox h-4 w-4 text-indigo-600 rounded border-slate-300 dark:border-slate-600"
                      checked={selectedKeys.has(row.retailer_upc)}
                      onChange={() => toggleOne(row.retailer_upc)}
                      aria-label={`Select ${row.retailer_upc}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-800 dark:text-slate-100 whitespace-nowrap">
                    {row.retailer_upc}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700 dark:text-slate-200">
                    {row.store_count}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                    {row.title || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-200 whitespace-nowrap">
                    {formatObserved(row.first_observed)}
                  </td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-200 whitespace-nowrap">
                    {formatObserved(row.last_observed)}
                  </td>
                  <td className="px-2 py-3 text-right whitespace-nowrap align-middle">
                    <button
                      type="button"
                      ref={(el) => {
                        rowBtnRefs.current[row.retailer_upc] = el;
                      }}
                      className="p-2 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                      aria-label="Row actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (rowMenuUpc === row.retailer_upc) {
                          setRowMenuUpc(null);
                        } else {
                          rowMenuAnchorRef.current =
                            rowBtnRefs.current[row.retailer_upc];
                          setRowMenuUpc(row.retailer_upc);
                        }
                      }}
                    >
                      <FaEllipsisVertical className="w-5 h-5 inline-block" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FixedDropdown
        show={rowMenuUpc != null}
        onClose={() => setRowMenuUpc(null)}
        triggerRef={rowMenuAnchorRef}
        menuRef={rowMenuPanelRef}
        estimatedHeight={88}
        align="right"
      >
        <button
          type="button"
          className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
          onClick={() => {
            setRowMenuUpc(null);
            const row = items.find((r) => r.retailer_upc === rowMenuUpc);
            setCreateModalSeed(toCreateProductState(row).autocreateSeed);
            setCreateModalOpen(true);
          }}
        >
          Create Product
        </button>
        <button
          type="button"
          className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
          onClick={() => {
            setRowMenuUpc(null);
            noop();
          }}
        >
          Create Product &amp; Link
        </button>
      </FixedDropdown>

      {totalPages > 1 && (
        <div className="mb-4">
          <PaginationNumeric
            currentPage={page - 1}
            onSelect={(zeroBasedPage) => setPage(zeroBasedPage + 1)}
            totalPages={totalPages}
            isFirstPage={page <= 1}
            isLastPage={page >= totalPages}
          />
        </div>
      )}

      <ModalBasic
        id="master-menu-unmatched-create-product"
        title="New product"
        modalOpen={createModalOpen}
        setModalOpen={(open) => {
          setCreateModalOpen(open);
          if (!open) setCreateModalSeed(null);
        }}
        width="7xl"
        className="h-[92vh] sm:h-[92vh]"
      >
        {createModalOpen ? (
          <MasterMenuCreateProduct
            embedded
            initialAutocreateSeed={createModalSeed}
            onClose={() => {
              setCreateModalOpen(false);
              setCreateModalSeed(null);
            }}
            onCreated={() => {
              setCreateModalOpen(false);
              setCreateModalSeed(null);
              fetchPage();
            }}
          />
        ) : null}
      </ModalBasic>
    </div>
  );
};

export default MasterMenuUnmatchedUPCs;
